import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { flatten } from 'ramda';
import useOnclickOutside from 'react-cool-onclickoutside';

import { ButtonBaseType, ID, Maybe } from 'src/models';
import { InputContext } from 'src/contexts/input.context';

import { BaseComponentProps, Dropdown } from 'src/components/Dropdown';
import { t } from 'src/tools/text.tools';
import { UseIcon } from 'src/components/UseIcon';
import { InputWithIcons } from 'src/components/Inputs';
import { TreeMenu } from 'src/components/TreeMenu';

import classes from './TreeSearchDropdown.module.scss';
import { render } from 'src/tools/render.tools';
import { Accent1Badge } from 'src/components/Badge';
import { P2 } from 'src/components/Typography';
import { ifTrue } from 'src/tools/logic.tools';
import { strictFinder } from 'src/tools/string.tools';

const TXT = t(['treeSearchDropdown']);

const EMPTY_VALUE: ID[] = [];

export interface TreeSearchDropdownBaseComponentProps extends BaseComponentProps {
  isDisabled?: boolean;
}

export interface TreeSearchDropdownProps {
  items?: ButtonBaseType[];
  renderBaseComponent?: (props: TreeSearchDropdownBaseComponentProps) => JSX.Element;
}

export function TreeSearchDropdown({
  items: propsItems,
  renderBaseComponent: propsRenderBaseComponent,
}: TreeSearchDropdownProps) {
  const {
    value = EMPTY_VALUE,
    onChange,
    classNames,
    isDisabled,
    isReadonly,
    onClick,
    customization = {},
    placeholder,
    items: contextItems,
    isLoading,
  } = useContext(InputContext);

  const badgesContainerRef = useRef<HTMLDivElement>(null);

  const [innerValue, setInnerValue] = useState<ID[]>(value);

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const {
    dropdownMinWidth = 'container-width',
    isSmallMenu,
    isHideSplits,
    isGreyMenu,
    multipleItemsLabel = TXT('multipleItems'),
  } = customization;

  const initialItems = useMemo(() => {
    function processItems(procItems?: ButtonBaseType[]): Maybe<ButtonBaseType[]> {
      if (!procItems) return;

      let lastInitiallyChecked: Maybe<ButtonBaseType>;

      const result = procItems
        .map(i => {
          const updatedItem = {
            ...i,
            isChecked: value.includes(i.id),
            items: processItems(i.items),
          };

          if (updatedItem.isChecked) lastInitiallyChecked = updatedItem;

          return updatedItem;
        })
        .sort((a, b) => {
          if (a.isChecked === b.isChecked) return 0;

          return a.isChecked ? -1 : 1;
        });

      // when not searching, add splitter after last INITIALLY checked item
      if (lastInitiallyChecked) lastInitiallyChecked.isSplitAfter = !isHideSplits;

      return result;
    }

    return processItems(propsItems ?? contextItems) ?? [];
  }, [contextItems, propsItems, value, isHideSplits]);

  const [search, setSearch] = useState<Maybe<string>>();

  const [items, currentItems] = useMemo(() => {
    const currentItems: ButtonBaseType[] = [];

    function processItems(procItems?: ButtonBaseType[]): Maybe<ButtonBaseType[]> {
      if (!procItems) return;

      const result = procItems.map(i => {
        const updatedItem = {
          ...i,
          isChecked: innerValue.includes(i.id),
          items: processItems(i.items),
          isSplitAfter: !isHideSplits && !search && i.isSplitAfter,
        };

        if (updatedItem.isChecked) currentItems.push(updatedItem);

        return updatedItem;
      });

      return search ? result.filter(i => strictFinder(search, i.label)) : result;
    }

    // when searching want to see all nested items only
    const result = search ? flatten(initialItems.map(i => processItems(i.items) ?? [])) : processItems(initialItems);

    return [result, currentItems];
  }, [search, initialItems, innerValue, isHideSplits]);

  const [isOpen, setIsOpen] = useState(false);

  const [isOverflowed, setIsOverflowed] = useState(false);

  const ref = useOnclickOutside(() => setIsOpen(false), {
    eventTypes: ['click'],
  });

  useLayoutEffect(() => {
    const element = badgesContainerRef.current;

    setIsOverflowed(!!innerValue.length && !!element && element.scrollWidth > element.clientWidth);
  }, [badgesContainerRef, innerValue]);

  const isItemsNotAvailable = !isDisabled && !isReadonly && !initialItems.length;

  const handleRequestOpenClose = useCallback(
    (val: boolean) => {
      setIsOpen(val);

      if (!val) {
        if (value !== innerValue) onChange(innerValue, true);

        setSearch('');
      }
    },
    [innerValue, onChange, value],
  );

  function handleOnItemClick(id: ID) {
    const isInValue = innerValue.includes(id);

    if (id === 'all') setInnerValue(isInValue ? [] : ['all']);
    else if (innerValue.includes('all')) {
      const ids: ID[] = [];

      const processItem = (i: ButtonBaseType) => {
        if (i.id !== 'all' && i.id !== id && !i.items?.length) ids.push(i.id);
        else i.items?.forEach(processItem);
      };

      initialItems.forEach(processItem);

      setInnerValue(ids);
    } else {
      const result = isInValue ? innerValue.filter((i: ID) => i !== id) : [...innerValue, id];

      setInnerValue(result);
    }
  }

  function handleInputOnChange(value: string) {
    setSearch(value);
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      if (!isItemsNotAvailable) {
        fn();
        onClick?.();
      }
    };
  }

  function handleIconClick(e: React.MouseEvent<HTMLButtonElement>) {
    e?.stopPropagation();
    setIsOpen(!isOpen);
  }

  function renderBadge(item: ButtonBaseType) {
    return (
      <Accent1Badge
        key={item.id}
        classNames={{ container: classes.badge }}
        label={ifTrue(item.id === 'all', TXT('all'), item.label)}
      />
    );
  }

  function renderMultipleLevelsLabel() {
    return <P2 className={classes.label}>{`${multipleItemsLabel} (${innerValue.length})`}</P2>;
  }

  function renderPlaceholder() {
    return <P2 className={clsx(classes.placeholder, 'black-light-text f-1')}>{placeholder}</P2>;
  }

  function renderBadges() {
    return (
      <div
        ref={badgesContainerRef}
        className={clsx('row items-p4 f-1 o-hidden', {
          [classes.overflowed]: isOverflowed,
        })}
      >
        {render(currentItems, renderBadge)}
      </div>
    );
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const extendedProps: TreeSearchDropdownBaseComponentProps = {
      ...baseComponentProps,
      onClick: getHandleOnClick(baseComponentProps.onClick),
      isDisabled: isDisabled || isItemsNotAvailable,
    };

    if (propsRenderBaseComponent) return propsRenderBaseComponent(extendedProps);

    return (
      <div
        data-testid='tree-search-dropdown'
        className={clsx(classes.container, classNames?.input, 'row a-center o-hidden w-100p', {
          [classes.focus]: isOpen,
          [classes.disabled]: extendedProps.isDisabled,
          'c-pointer': !extendedProps.isDisabled,
        })}
        onClick={extendedProps.onClick}
        onFocus={extendedProps.onFocus}
        tabIndex={0}
      >
        {ifTrue(!innerValue.length, renderPlaceholder, renderBadges)}
        {ifTrue(isOverflowed, renderMultipleLevelsLabel)}
        <UseIcon className='default-icon' iconId={isOpen ? 'chevron-up' : 'chevron-down'} onClick={handleIconClick} />
      </div>
    );
  }

  return (
    <div ref={ref}>
      <Dropdown
        classNames={{ container: 'w-100p', dropdown: classes.dropdown }}
        renderBaseComponent={renderBaseComponent}
        onRequestOpenClose={handleRequestOpenClose}
        isOpen={isOpen && !isReadonly}
        dropdownMinWidth={dropdownMinWidth}
        tooltip={ifTrue(isItemsNotAvailable, TXT('dropdownValuesIsNotAvailable'))}
        isLoading={isLoading}
      >
        <div className={clsx(classes['input-wrap'], { [classes.small]: isSmallMenu })}>
          <InputWithIcons
            value={search}
            placeholder={TXT('search')}
            onChange={handleInputOnChange}
            leftIconId='search'
            isXSmall
            autoFocus
          />
        </div>
        <TreeMenu
          className={clsx({ [classes['grey-menu']]: isGreyMenu })}
          items={items}
          onItemClick={handleOnItemClick}
          isSmall={isSmallMenu}
        />
      </Dropdown>
    </div>
  );
}
